import React from 'react';
import {
  ProductSummary,
  ProductSummaryProps,
} from '@kizi/comparator-components';
import { useField, IProduct, usePage } from '@kizi/comparator-core';
import { CostItem } from '../../utils/extraOptions';

interface ProductSummaryWrapperProps extends ProductSummaryProps {
  selectedProduct: string;
  coverage: string;
}

export interface SummaryCostInfo {
  name: string;
  rates: CostItem[];
  fieldName: string;
}

const modifyPrice = (value: number | undefined, replace?: boolean) => {
  const result = !value ? Number(value) : value.toFixed(2);
  return replace && typeof result === 'string'
    ? result.replace('.', ',')
    : result;
};

const parseRates = (rates: CostItem[]) => {
  if (rates.length > 1) {
    return rates[1]?.rate || 0;
  }

  return rates[0]?.rate || 0;
};

const transformDiscountTerm = (term: number) => {
  if (term === 1) {
    return 'maand';
  }
  return `${term} maanden`;
};

const shouldMultiply = (
  multiply: boolean | undefined,
  num1: number,
  num2: number,
) => {
  return multiply ? num1 : num2;
};

const replaceRate = (
  rateWithDiscount: number,
  rateWithoutDiscount: number,
  term: number,
) => {
  if (rateWithDiscount) {
    return (rateWithoutDiscount - rateWithDiscount) * term;
  }

  return term * rateWithoutDiscount;
};

const hasFirstMonthsDiscount = (product: IProduct, applyDiscount: boolean) =>
  product.priceInfo.priceList[0].amount !== product.priceInfo.total &&
  applyDiscount;

const extractSummaryDataFromProduct = (
  product: IProduct,
  monthlyCosts: SummaryCostInfo[] = [],
  onceOffCosts: SummaryCostInfo[] = [],
  applyDiscount: boolean = true,
  discountDurationInMonths,
  discountTariff,
) => {
  const amountOfMonthDiscount =
    discountDurationInMonths || product.priceInfo?.priceList[0].periodInMonth;
  const monthDiscount =
    discountTariff || product.priceInfo?.priceList[0].amount;
  const calculateMonthlyTotal = (replace: boolean, multiply?: boolean) =>
    modifyPrice(
      product.priceInfo.total * shouldMultiply(multiply, 12, 1) +
        monthlyCosts.reduce(
          (acc, { rates }) =>
            acc +
            shouldMultiply(multiply, parseRates(rates) * 12, parseRates(rates)),
          0,
        ),
      replace,
    );
  const calculateOnceOffTotal = (replace: boolean) =>
    modifyPrice(
      Number(product.priceInfo.connection) +
        Number(product.priceInfo.postage) +
        onceOffCosts.reduce((acc, { rates }) => acc + parseRates(rates), 0),
      replace,
    );

  const calculateDiscountTotal = (replace: boolean) =>
    modifyPrice(
      (hasFirstMonthsDiscount(product, applyDiscount)
        ? (product.priceInfo.total - monthDiscount) * amountOfMonthDiscount
        : 0) +
        (product.priceInfo.discountIncluded || 10) +
        monthlyCosts.reduce(
          (acc, { rates }) =>
            acc +
            (rates.length > 1
              ? replaceRate(rates[0].rate, rates[1].rate, rates[0].term)
              : 0),
          0,
        ),
      replace,
    );

  const parsePrices = (data: SummaryCostInfo[]) =>
    data.map(({ name, rates }) => ({
      label: `+ ${name.replace(/&euro;/gi, '€')}`,
      value: `€ ${modifyPrice(parseRates(rates), true)}`,
      textContainerProps: {
        paddingLeft: 2,
      },
      details:
        rates.length > 1
          ? {
              title: '',
              containerProps: {
                mt: 0,
              },
              rows: [
                {
                  label: `Eerste ${transformDiscountTerm(rates[0].term)}`,
                  value: `€ ${modifyPrice(rates[0].rate)}`,
                  textContainerProps: {
                    color: 'second.0',
                  },
                },
              ],
            }
          : undefined,
    }));
  const isOS = product.type.includes('os-');
  const installationCost = isOS
    ? product.priceInfo.installation
    : product.priceInfo.installation.filter((cost) => cost.required);
  const totalinstallationCost = isOS
    ? installationCost
    : installationCost.reduce((acc, curr) => {
        return acc + curr.rate;
      }, 0);

  const installationCostRows = installationCost.map(({ name, rate }) => {
    return {
      label: name,
      value: `€ ${modifyPrice(rate, true)}`,
    };
  });

  return {
    logo: {
      name: 'logo',
      url: product.image,
    },
    title: product.provider.name,
    groups: [
      {
        rows: [
          {
            label: 'Maandelijkse kosten',
            textContainerProps: {
              fontWeight: 1,
            },
          },
          {
            label: product.subscriptionName,
            value: `€ ${modifyPrice(product.priceInfo.total, true)}`,
          },
          {
            label: hasFirstMonthsDiscount(product, applyDiscount)
              ? `Eerste ${amountOfMonthDiscount} maanden voor € ${modifyPrice(
                  monthDiscount,
                  true,
                )}`
              : '',
            textContainerProps: {
              color: 'second.0',
              display: 'block',
            },
          },
          ...parsePrices(monthlyCosts),
          {
            label: 'Totaal per maand',
            value: `€ ${calculateMonthlyTotal(true)}`,
          },
        ],
      },
      {
        rows: [
          {
            label: 'Eenmalige kosten',
            textContainerProps: {
              fontWeight: 1,
            },
          },
          ...installationCostRows,
          {
            label: 'Eenmalige aansluitkosten',
            value: `€ ${modifyPrice(product.priceInfo.connection, true)}`,
          },
          {
            label: 'Verzend- en administratiekosten',
            value: product.priceInfo.postage
              ? `€ ${modifyPrice(product.priceInfo.postage, true)}`
              : '€ 0',
          },
          ...parsePrices(onceOffCosts),
        ],
      },
      {
        rows: [
          {
            label: 'Kortingen',
            textContainerProps: {
              fontWeight: 1,
            },
          },
          {
            label: hasFirstMonthsDiscount(product, applyDiscount)
              ? `Korting eerste ${amountOfMonthDiscount} maanden`
              : '',
            value: hasFirstMonthsDiscount(product, applyDiscount)
              ? `- € ${modifyPrice(
                  (product.priceInfo.total - monthDiscount) *
                    amountOfMonthDiscount,
                )}`
              : '',
          },
          {
            label: 'Eenmalige korting via ons',
            value: `- € ${
              product.priceInfo.discountIncluded
                ? modifyPrice(product.priceInfo.discountIncluded, true)
                : modifyPrice(10, true)
            }`,
          },
        ],
      },
      {
        rows: [
          {
            label: 'Totaal',
            textContainerProps: {
              fontWeight: 1,
            },
          },
          {
            label: 'Totale jaarlijkse kosten',
            value: ` € ${calculateMonthlyTotal(true, true)}`,
          },
          {
            label: 'Totale eenmalige kosten',
            value: `€ ${calculateOnceOffTotal(true)}`,
          },
          {
            label: 'Totale Korting',
            value: `- € ${calculateDiscountTotal(true)}`,
          },
        ],
      },
      {
        rows: [
          {
            label: 'Totale kosten eerste jaar',
            value: `€ ${modifyPrice(
              Number(calculateMonthlyTotal(false, true)) +
                Number(calculateOnceOffTotal(false)) -
                Number(calculateDiscountTotal(false)) +
                totalinstallationCost,
              true,
            )}`,
            textContainerProps: {
              fontWeight: 1,
            },
          },
        ],
      },
    ],
  };
};

export const ProductSummaryWrapper = React.memo<ProductSummaryWrapperProps>(
  ({ selectedProduct, saveForLater, ...props }) => {
    const selectedProductField = useField(selectedProduct);
    const { state } = usePage();

    return (
      <ProductSummary
        saveForLater={saveForLater}
        {...props}
        {...extractSummaryDataFromProduct(
          JSON.parse(selectedProductField?.value as string) || '',
          state?.monthly as SummaryCostInfo[],
          state?.onceOff as SummaryCostInfo[],
          state?.applyDiscount as boolean,
          state?.discountDurationInMonths,
          state?.discountTariff,
        )}
      />
    );
  },
);
